// ============================================================
//  src/pages/bots/tabs/EndpointsTab.js
//  Custom code endpoints: list, editor (Monaco), test runner, logs.
//  Styles: ./EndpointsTab.css (scoped under .epx-*)
// ============================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Editor from '@monaco-editor/react';
import {
  listEndpoints, getEndpoint, createEndpoint, updateEndpoint, toggleEndpoint,
  regenerateEndpointSlug, deleteEndpoint, listEndpointLogs, clearEndpointLogs,
  getEndpointErrorLog, clearEndpointErrorLog,
} from '../../../api/client';
import { copyToClipboard, formatDate, timeAgo } from '../../../utils/helpers';
import './EndpointsTab.css';

const LANGUAGES = [
  {
    value: 'php', label: 'PHP', short: 'PHP', monaco: 'php', color: '#777bb3', ready: true,
    help: [
      ['$method, $headers, $query', 'The incoming request.'],
      ['$body, $json', 'Raw body, and the body decoded as JSON.'],
      ['$bot', 'bId, bName, bToken, bPhoneNumberId, bWhatsappId, bWebhookToken.'],
      ['$panel', "Your bot's Panel values — e.g. $panel['api_key']. Keep secrets here, not in code."],
      ["debug_log('msg', $data)", "Writes a line to this call's Debug Log. error_log() is captured too."],
      ['echo …', 'Your response. Defaults to JSON / 200 — use header() and http_response_code() to change it.'],
    ],
    template:
`<?php
// Available: $method, $headers, $query, $body, $json, $bot, $panel
// $bot   = ['bId','bName','bToken','bPhoneNumberId','bWhatsappId','bWebhookToken']
// $panel = your bot's Panel key/value pairs, e.g. $panel['api_key']

// Anything you pass to debug_log() shows up in this endpoint's
// "Logs" → Debug Log panel for the matching call, with a timestamp.
debug_log('endpoint hit', ['method' => $method, 'query' => $query]);

header('Content-Type: application/json');
echo json_encode([
    'status'   => 'ok',
    'method'   => $method,
    'received' => $json,
]);
`,
  },
  {
    value: 'json', label: 'JSON', short: '{ }', monaco: 'json', color: '#2563eb', ready: true,
    help: [['Static JSON', 'Returned exactly as written on every call — no code runs.']],
    template: `{
  "data": {
    "status": "active",
    "version": "v3.0"
  }
}`,
  },
  {
    value: 'javascript', label: 'JavaScript', short: 'JS', monaco: 'javascript', color: '#caa800', ready: false,
    help: [['Coming soon', 'JavaScript needs a Node runtime, which isn’t available on shared hosting yet. You can save it, but calls return 501 until it ships.']],
    template: `// Coming soon on this hosting plan.
// Saved, but the endpoint will respond with 501 until JS execution ships.
`,
  },
  {
    value: 'python', label: 'Python', short: 'PY', monaco: 'python', color: '#3776ab', ready: false,
    help: [['Coming soon', 'Python needs a Python runtime, which isn’t available on shared hosting yet. You can save it, but calls return 501 until it ships.']],
    template: `# Coming soon on this hosting plan.
# Saved, but the endpoint will respond with 501 until Python execution ships.
`,
  },
  {
    value: 'other', label: 'Plain text', short: 'TXT', monaco: 'plaintext', color: '#6b7280', ready: true,
    help: [['Plain text', 'Returned exactly as written, with a text/plain response.']],
    template: 'OK',
  },
];

const langMeta = (value) => LANGUAGES.find(l => l.value === value) || LANGUAGES[0];
const FILE_EXT = { php: 'php', json: 'json', javascript: 'js', python: 'py', other: 'txt' };
const fileNameFor = (name, lang) => `${(name || 'endpoint').trim().replace(/[^a-z0-9-_]+/gi, '-').replace(/^-+|-+$/g, '') || 'endpoint'}.${FILE_EXT[lang] || 'txt'}`;

/** lDebugLog is stored as a JSON array of {t, src, msg, data?}. Falls back to
 *  treating the raw string as one line if it's ever not valid JSON. */
function parseDebugLog(raw) {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [{ t: null, src: 'app', msg: raw }];
  }
}
function isLogError(log) {
  return !!log.lError || (log.lStatusCode && log.lStatusCode >= 400);
}
function logMatchesFilter(log, filter) {
  const error = isLogError(log);
  if (filter.status === 'ok' && error) return false;
  if (filter.status === 'error' && !error) return false;
  if (filter.q) {
    const q = filter.q.toLowerCase();
    const haystack = [
      log.lMethod, log.lStatusCode, log.lError, log.lRequestBody, log.lResponseBody,
      log.lWarnings, log.lDebugLog, log.lQueryString, log.lHeaders,
    ].filter(Boolean).join(' ').toLowerCase();
    if (!haystack.includes(q)) return false;
  }
  return true;
}
function formatDebugLineForExport(entry) {
  const t = entry.t != null ? `+${entry.t}ms` : '      ';
  const src = entry.src === 'error_log' ? 'PHP' : 'APP';
  return `    [${t}] [${src}] ${entry.msg}${entry.data ? ' ' + entry.data : ''}`;
}
function formatLogForExport(log) {
  const error = isLogError(log);
  const lines = [
    `[${formatDate(log.lCreatedAt, true)}] ${log.lMethod || 'GET'} · ${log.lStatusCode || '—'} · ` +
    `${log.lDurationMs != null ? log.lDurationMs + 'ms' : '—'} · ${error ? 'ERROR' : 'OK'}`,
  ];
  if (log.lError) lines.push(`  Error: ${log.lError}`);
  if (log.lStackTrace) lines.push(`  Stack trace: ${log.lStackTrace}`);
  const debugEntries = parseDebugLog(log.lDebugLog);
  if (debugEntries.length) {
    lines.push('  Debug Log:');
    debugEntries.forEach((e) => lines.push(formatDebugLineForExport(e)));
  }
  if (log.lWarnings) {
    lines.push('  Warnings:');
    try {
      const w = JSON.parse(log.lWarnings);
      (Array.isArray(w) ? w : [log.lWarnings]).forEach((line) => lines.push(`    ${line}`));
    } catch {
      lines.push(`    ${log.lWarnings}`);
    }
  }
  lines.push(`  Request:  ${log.lRequestBody || '(empty)'}`);
  lines.push(`  Response: ${log.lResponseBody || '(empty)'}`);
  return lines.join('\n');
}
function warningsText(raw) {
  try { const a = JSON.parse(raw); return Array.isArray(a) ? a.join('\n') : raw; } catch { return raw; }
}
/** Pretty-print JSON if it parses, else return as-is. */
function pretty(str) {
  if (!str) return '';
  try { return JSON.stringify(JSON.parse(str), null, 2); } catch { return String(str); }
}
function downloadText(filename, text) {
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
const safeName = (n) => (n || 'endpoint').replace(/[^a-z0-9-_]+/gi, '_');
const statusTone = (code) => (!code ? 'muted' : code >= 500 ? 'err' : code >= 400 ? 'warn' : code >= 300 ? 'info' : 'ok');

// ── Small building blocks ─────────────────────────────────────
function LangTile({ lang, size = 'md' }) {
  const m = langMeta(lang);
  return <span className={`epx-lang ${size}`} style={{ '--lang': m.color }} aria-label={m.label}>{m.short}</span>;
}

function Switch({ checked, onChange, label }) {
  return (
    <label className="epx-switch" title={checked ? 'Active — click to disable' : 'Disabled — click to enable'}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      <span className="epx-switch-track" aria-hidden="true" />
      <span className="epx-switch-label">{label ?? (checked ? 'Active' : 'Off')}</span>
    </label>
  );
}

function CopyButton({ text, label = 'Copy', onCopied, className = '' }) {
  const [done, setDone] = useState(false);
  return (
    <button type="button" className={`epx-copy ${done ? 'is-done' : ''} ${className}`}
      onClick={async (e) => { e.stopPropagation(); await copyToClipboard(text || ''); setDone(true); onCopied?.(); setTimeout(() => setDone(false), 1500); }}>
      <i className={`fa-${done ? 'solid fa-check' : 'regular fa-copy'}`} /><span>{done ? 'Copied' : label}</span>
    </button>
  );
}

function CodeBlock({ title, text, empty = '(empty)', tone, onCopied }) {
  const value = pretty(text);
  return (
    <div className={`epx-block ${tone ? `is-${tone}` : ''}`}>
      <div className="epx-block-head">
        <span>{title}</span>
        {value && <CopyButton text={value} onCopied={onCopied} />}
      </div>
      <pre className="epx-pre">{value || <em>{empty}</em>}</pre>
    </div>
  );
}

function RowMenu({ onRegenerate, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  return (
    <div className="epx-menu" ref={ref}>
      <button type="button" className="epx-icon-btn" aria-label="More actions" aria-expanded={open} onClick={() => setOpen(o => !o)}>
        <i className="fa-solid fa-ellipsis" />
      </button>
      {open && (
        <div className="epx-menu-pop" role="menu">
          <button type="button" role="menuitem" onClick={() => { setOpen(false); onRegenerate(); }}>
            <i className="fa-solid fa-rotate" /> Regenerate URL
          </button>
          <button type="button" role="menuitem" className="is-danger" onClick={() => { setOpen(false); onDelete(); }}>
            <i className="fa-regular fa-trash-can" /> Delete endpoint
          </button>
        </div>
      )}
    </div>
  );
}

function useBodyLock(active) {
  useEffect(() => {
    if (!active) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [active]);
}

// ============================================================
export default function EndpointsTab({ bot }) {
  const bId = bot?.bId;

  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const [editing, setEditing] = useState(null);   // endpoint object being edited, or fresh object for new
  const [saving, setSaving] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [editorHeight, setEditorHeight] = useState(440);
  const savedCodeRef = useRef({ eName: '', eLanguage: '', eCode: '' });
  const editorRef = useRef(null);

  const [logsFor, setLogsFor] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);
  const [logFilter, setLogFilter] = useState({ status: 'all', q: '' });
  const [liveTail, setLiveTail] = useState(false);
  const [logsView, setLogsView] = useState('calls'); // 'calls' | 'raw'
  const [rawLog, setRawLog] = useState('');
  const [rawLogLoading, setRawLogLoading] = useState(false);

  const [testFor, setTestFor] = useState(null);     // endpoint being tested
  const [testReq, setTestReq] = useState({ method: 'GET', body: '' });
  const [testRes, setTestRes] = useState(null);
  const [testing, setTesting] = useState(false);

  const [confirmState, setConfirmState] = useState(null);

  useBodyLock(!!editing || !!logsFor || !!testFor);

  const showToast = (msg, type = 'success') => {
    clearTimeout(toastTimer.current);
    setToast({ msg, type });
    toastTimer.current = setTimeout(() => setToast(null), 2600);
  };

  const requestConfirm = ({ title = 'Are you sure?', message, confirmLabel = 'Confirm', danger = false, onConfirm }) => {
    setConfirmState({ title, message, confirmLabel, danger, onConfirm });
  };
  const runConfirm = async () => {
    if (!confirmState) return;
    const { onConfirm } = confirmState;
    setConfirmState((s) => ({ ...s, busy: true }));
    try { await onConfirm(); } finally { setConfirmState(null); }
  };

  const load = useCallback(async () => {
    if (!bId) return;
    setLoading(true);
    try {
      const { data } = await listEndpoints(bId);
      setEndpoints(data.endpoints || []);
    } catch (e) {
      showToast(e?.response?.data?.message || 'Failed to load endpoints', 'error');
    } finally {
      setLoading(false);
    }
  }, [bId]);

  useEffect(() => { load(); }, [load]);

  // ── Editor ─────────────────────────────────────────────────
  const openNew = () => {
    const fresh = { eId: null, eName: '', eLanguage: 'php', eCode: langMeta('php').template };
    savedCodeRef.current = { eName: fresh.eName, eLanguage: fresh.eLanguage, eCode: fresh.eCode };
    setShowHelp(false);
    setEditing(fresh);
  };

  const openEdit = async (ep) => {
    setShowHelp(false);
    setEditing({ ...ep, eCode: '', __loading: true });
    try {
      const { data } = await getEndpoint(bId, ep.eId);
      savedCodeRef.current = { eName: data.endpoint.eName, eLanguage: data.endpoint.eLanguage, eCode: data.endpoint.eCode };
      setEditing({ ...data.endpoint, __loading: false });
    } catch (e) {
      showToast(e?.response?.data?.message || 'Failed to load endpoint code', 'error');
      setEditing(null);
    }
  };

  const isDirty = !!editing && !editing.__loading && (
    savedCodeRef.current.eName !== editing.eName
    || savedCodeRef.current.eLanguage !== editing.eLanguage
    || savedCodeRef.current.eCode !== editing.eCode
  );

  const closeEditor = () => {
    if (saving) return;
    if (isDirty && editing.eId) {
      requestConfirm({
        title: 'Discard changes?',
        message: 'You have unsaved changes to this endpoint.',
        confirmLabel: 'Discard',
        danger: true,
        onConfirm: async () => setEditing(null),
      });
      return;
    }
    setEditing(null);
  };

  const handleLanguageChange = (lang) => {
    setEditing((prev) => {
      const isUntouched = !prev.eId && (prev.eCode === '' || prev.eCode === langMeta(prev.eLanguage).template);
      return { ...prev, eLanguage: lang, eCode: isUntouched ? langMeta(lang).template : prev.eCode };
    });
  };

  function onEditorResizeMouseDown(e) {
    e.preventDefault();
    const startY = e.clientY, startH = editorHeight;
    const onMove = (mv) => setEditorHeight(Math.max(220, Math.min(900, startH + mv.clientY - startY)));
    const onUp   = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  const save = async () => {
    if (!editing.eName?.trim()) { showToast('Give this endpoint a name', 'error'); return; }

    if (editing.eId) {
      const base = savedCodeRef.current;
      const unchanged = base.eName === editing.eName && base.eLanguage === editing.eLanguage && base.eCode === editing.eCode;
      if (unchanged) { showToast('No changes to save', 'info'); setEditing(null); return; }
    }

    setSaving(true);
    try {
      if (editing.eId) {
        await updateEndpoint(bId, editing.eId, { eName: editing.eName, eLanguage: editing.eLanguage, eCode: editing.eCode });
        showToast('Endpoint saved');
      } else {
        await createEndpoint(bId, { eName: editing.eName, eLanguage: editing.eLanguage, eCode: editing.eCode });
        showToast('Endpoint created');
      }
      savedCodeRef.current = { eName: editing.eName, eLanguage: editing.eLanguage, eCode: editing.eCode };
      setEditing(null);
      load();
    } catch (e) {
      showToast(e?.response?.data?.message || 'Failed to save endpoint', 'error');
    } finally {
      setSaving(false);
    }
  };
  const saveRef = useRef(save);
  saveRef.current = save;

  // ── Row actions ────────────────────────────────────────────
  const handleToggle = async (ep, active) => {
    setEndpoints((prev) => prev.map(e => e.eId === ep.eId ? { ...e, eStatus: active ? 'active' : 'disabled' } : e));
    try {
      await toggleEndpoint(bId, ep.eId, active);
      showToast(active ? 'Endpoint enabled' : 'Endpoint disabled', 'info');
    } catch {
      setEndpoints((prev) => prev.map(e => e.eId === ep.eId ? { ...e, eStatus: ep.eStatus } : e));
      showToast('Failed to update status', 'error');
    }
  };

  const handleRegenerate = (ep) => {
    requestConfirm({
      title: 'Regenerate URL?',
      message: 'The old URL stops working immediately — update it anywhere it’s in use (Flows, webhooks, commands) first.',
      confirmLabel: 'Regenerate URL',
      danger: true,
      onConfirm: async () => {
        try {
          const { data } = await regenerateEndpointSlug(bId, ep.eId);
          setEndpoints((prev) => prev.map(e => e.eId === ep.eId ? { ...e, url: data.url } : e));
          showToast('New URL generated');
        } catch {
          showToast('Failed to regenerate URL', 'error');
        }
      },
    });
  };

  const handleDelete = (ep) => {
    requestConfirm({
      title: 'Delete endpoint?',
      message: <>Delete <strong>“{ep.eName}”</strong>? Anything calling its URL will start failing. This can’t be undone.</>,
      confirmLabel: 'Delete endpoint',
      danger: true,
      onConfirm: async () => {
        try {
          await deleteEndpoint(bId, ep.eId);
          setEndpoints((prev) => prev.filter(e => e.eId !== ep.eId));
          showToast('Endpoint deleted');
        } catch {
          showToast('Failed to delete endpoint', 'error');
        }
      },
    });
  };

  // ── Test runner ────────────────────────────────────────────
  const openTest = (ep) => {
    setTestFor(ep);
    setTestRes(null);
    setTestReq({ method: 'GET', body: ep.eLanguage === 'php' ? '{\n  "hello": "world"\n}' : '' });
  };
  const runTest = async () => {
    if (!testFor) return;
    setTesting(true);
    setTestRes(null);
    const started = performance.now();
    try {
      const opts = { method: testReq.method };
      if (testReq.method !== 'GET' && testReq.body.trim()) {
        opts.body = testReq.body;
        opts.headers = { 'Content-Type': 'application/json' };
      }
      const res = await fetch(testFor.url, opts);
      const text = await res.text();
      setTestRes({
        ok: res.ok, status: res.status, statusText: res.statusText,
        ms: Math.round(performance.now() - started),
        type: res.headers.get('content-type') || '',
        body: text,
      });
    } catch (e) {
      setTestRes({ error: e.message, ms: Math.round(performance.now() - started) });
    } finally {
      setTesting(false);
    }
  };

  // ── Logs ───────────────────────────────────────────────────
  const openLogs = async (ep) => {
    setLogsFor(ep);
    setLogsLoading(true);
    setExpandedLog(null);
    setLogFilter({ status: ep.eLastStatus === 'error' ? 'error' : 'all', q: '' });
    setLiveTail(false);
    setLogsView('calls');
    setRawLog('');
    try {
      const { data } = await listEndpointLogs(bId, ep.eId);
      setLogs(data.logs || []);
    } catch {
      showToast('Failed to load logs', 'error');
    } finally {
      setLogsLoading(false);
    }
  };
  const closeLogs = () => { setLogsFor(null); setLiveTail(false); };

  const loadRawLog = useCallback(async (silent = false) => {
    if (!logsFor) return;
    if (!silent) setRawLogLoading(true);
    try {
      const { data } = await getEndpointErrorLog(bId, logsFor.eId);
      setRawLog(data.log || '');
    } catch {
      if (!silent) showToast('Failed to load error log', 'error');
    } finally {
      if (!silent) setRawLogLoading(false);
    }
  }, [bId, logsFor]);

  const switchLogsView = (view) => {
    setLogsView(view);
    if (view === 'raw' && !rawLog) loadRawLog();
  };

  const refreshLogsSilently = useCallback(async () => {
    if (!logsFor) return;
    try {
      const { data } = await listEndpointLogs(bId, logsFor.eId);
      setLogs(data.logs || []);
    } catch { /* silent */ }
  }, [bId, logsFor]);

  useEffect(() => {
    if (!logsFor || !liveTail) return undefined;
    const id = setInterval(() => {
      if (logsView === 'raw') loadRawLog(true);
      else refreshLogsSilently();
    }, 4000);
    return () => clearInterval(id);
  }, [logsFor, liveTail, logsView, refreshLogsSilently, loadRawLog]);

  // Escape closes the top-most layer
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || confirmState) return;
      if (testFor) setTestFor(null);
      else if (logsFor) closeLogs();
      else if (editing) closeEditor();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const handleClearLogs = () => {
    if (!logsFor) return;
    requestConfirm({
      title: 'Clear all call logs?',
      message: 'Every logged call for this endpoint will be removed. This can’t be undone.',
      confirmLabel: 'Clear logs',
      danger: true,
      onConfirm: async () => {
        try { await clearEndpointLogs(bId, logsFor.eId); setLogs([]); showToast('Logs cleared'); }
        catch { showToast('Failed to clear logs', 'error'); }
      },
    });
  };
  const handleClearRawLog = () => {
    if (!logsFor) return;
    requestConfirm({
      title: 'Clear error log?',
      message: 'Clear the error_log file for this endpoint? This can’t be undone.',
      confirmLabel: 'Clear error log',
      danger: true,
      onConfirm: async () => {
        try { await clearEndpointErrorLog(bId, logsFor.eId); setRawLog(''); showToast('Error log cleared'); }
        catch { showToast('Failed to clear error log', 'error'); }
      },
    });
  };

  const errorCount   = logs.filter(isLogError).length;
  const okCount      = logs.length - errorCount;
  const filteredLogs = logs.filter((log) => logMatchesFilter(log, logFilter));
  const durations    = logs.map(l => l.lDurationMs).filter(v => v != null);
  const avgMs        = durations.length ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : null;
  const maxMs        = durations.length ? Math.max(...durations) : 1;

  const activeCount = endpoints.filter(e => e.eStatus === 'active').length;
  const errorEps    = endpoints.filter(e => e.eLastStatus === 'error').length;

  if (!bId) return null;

  return (
    <div className="epx">
      {/* ── Header ── */}
      <header className="epx-head">
        <div className="epx-head-copy">
          <span className="epx-eyebrow"><i className="fa-solid fa-code" /> Endpoints</span>
          <h2>Your bot’s backend, built in</h2>
          <p>Write PHP or JSON right here and get a live URL — no server needed. Use it as a Flow <b>Endpoint URI</b>, call it from a command’s HTTP action, or point any webhook at it.</p>
        </div>
        <button type="button" className="epx-btn primary lg" onClick={openNew}><i className="fa-solid fa-plus" /> New endpoint</button>
      </header>

      {!loading && endpoints.length > 0 && (
        <div className="epx-stats">
          <div><b>{endpoints.length}</b><span>endpoint{endpoints.length !== 1 ? 's' : ''}</span></div>
          <div><b className="ok">{activeCount}</b><span>active</span></div>
          <div className={errorEps ? 'has-err' : ''}><b className={errorEps ? 'err' : ''}>{errorEps}</b><span>{errorEps === 1 ? 'needs attention' : 'need attention'}</span></div>
        </div>
      )}

      {/* ── List ── */}
      {loading ? (
        <div className="epx-list">
          {[0, 1].map(i => <div key={i} className="epx-card epx-skel"><span className="sk tile" /><div><span className="sk w40" /><span className="sk w90" /><span className="sk w30" /></div></div>)}
        </div>
      ) : endpoints.length === 0 ? (
        <div className="epx-empty">
          <div className="epx-empty-art" aria-hidden="true">
            <div className="epx-empty-code">
              <span><em>POST</em> /run/a8Fk2…</span>
              <span className="c">{'{'} <i>"status"</i>: <b>"ok"</b> {'}'}</span>
            </div>
          </div>
          <h3>Create your first endpoint</h3>
          <p>Handle WhatsApp Flow data exchange, process payments, or return JSON to your commands — all hosted for you.</p>
          <div className="epx-empty-uses">
            <span><i className="fa-solid fa-bolt" /> Flow data exchange</span>
            <span><i className="fa-solid fa-terminal" /> Command HTTP actions</span>
            <span><i className="fa-solid fa-satellite-dish" /> Incoming webhooks</span>
          </div>
          <button type="button" className="epx-btn primary" onClick={openNew}><i className="fa-solid fa-plus" /> New endpoint</button>
        </div>
      ) : (
        <div className="epx-list">
          {endpoints.map((ep) => {
            const active   = ep.eStatus === 'active';
            const hasError = ep.eLastStatus === 'error';
            const neverRan = !ep.eLastRunAt;
            const meta     = langMeta(ep.eLanguage);
            return (
              <article key={ep.eId} className={`epx-card ${hasError ? 'has-error' : ''} ${!active ? 'is-off' : ''}`}>
                <div className="epx-card-top">
                  <LangTile lang={ep.eLanguage} />
                  <div className="epx-card-title">
                    <h3>{ep.eName}</h3>
                    <div className="epx-card-meta">
                      <span>{meta.label}{!meta.ready && ' · coming soon'}</span>
                      <span className="epx-dot" />
                      {neverRan ? (
                        <span className="epx-muted">Never called</span>
                      ) : (
                        <span className={`epx-last ${hasError ? 'is-err' : 'is-ok'}`} title={formatDate(ep.eLastRunAt, true)}>
                          <i className={`fa-solid ${hasError ? 'fa-circle-exclamation' : 'fa-circle-check'}`} />
                          {hasError ? 'Last call failed' : 'Last call OK'} · {timeAgo(ep.eLastRunAt)}
                        </span>
                      )}
                    </div>
                  </div>
                  <Switch checked={active} onChange={(v) => handleToggle(ep, v)} />
                </div>

                <div className="epx-url" onClick={() => { copyToClipboard(ep.url); showToast('URL copied'); }} role="button" tabIndex={0}
                  onKeyDown={e => { if (e.key === 'Enter') { copyToClipboard(ep.url); showToast('URL copied'); } }} title="Click to copy">
                  <span className="epx-url-method">ANY</span>
                  <code>{ep.url}</code>
                  <CopyButton text={ep.url} label="Copy" className="epx-url-copy" />
                </div>

                <div className="epx-card-actions">
                  <button type="button" className="epx-btn ghost sm" onClick={() => openTest(ep)} disabled={!active}
                    title={active ? 'Send a test request' : 'Enable the endpoint to test it'}>
                    <i className="fa-solid fa-play" /> Test
                  </button>
                  <button type="button" className={`epx-btn sm ${hasError ? 'danger-soft' : 'ghost'}`} onClick={() => openLogs(ep)}>
                    <i className={`fa-solid ${hasError ? 'fa-bug' : 'fa-list-ul'}`} /> {hasError ? 'Debug' : 'Logs'}
                  </button>
                  <button type="button" className="epx-btn dark sm" onClick={() => openEdit(ep)}>
                    <i className="fa-solid fa-pen" /> Edit code
                  </button>
                  <RowMenu onRegenerate={() => handleRegenerate(ep)} onDelete={() => handleDelete(ep)} />
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* ── Editor ─────────────────────────────────────────── */}
      {editing && (
        <div className="epx-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) closeEditor(); }}>
          <div
            className="epx-ide"
            role="dialog"
            aria-label={editing.eId ? 'Edit endpoint' : 'New endpoint'}
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
                e.preventDefault();
                if (!saving && !editing.__loading) saveRef.current();
              }
            }}
          >
            <div className="epx-ide-bar">
              <button type="button" className="epx-ide-close" onClick={closeEditor} aria-label="Close editor"><i className="fa-solid fa-xmark" /></button>
              <div className="epx-ide-name">
                <LangTile lang={editing.eLanguage} size="sm" />
                <input
                  value={editing.eName}
                  onChange={(e) => setEditing({ ...editing, eName: e.target.value })}
                  placeholder="Name your endpoint, e.g. flow-data-exchange"
                  aria-label="Endpoint name"
                  autoFocus={!editing.eId}
                />
                {isDirty && <span className="epx-dirty" title="Unsaved changes" />}
              </div>
              <div className="epx-ide-actions">
                <span className="epx-kbd"><kbd>Ctrl</kbd><kbd>S</kbd></span>
                <button type="button" className="epx-btn primary" onClick={save} disabled={saving || editing.__loading}>
                  {saving ? <><span className="epx-spin" /> Saving…</> : <><i className="fa-solid fa-floppy-disk" /> {editing.eId ? 'Save' : 'Create'}</>}
                </button>
              </div>
            </div>

            <div className="epx-ide-tools">
              <div className="epx-seg" role="radiogroup" aria-label="Language">
                {LANGUAGES.map((l) => (
                  <button key={l.value} type="button" role="radio" aria-checked={editing.eLanguage === l.value}
                    className={editing.eLanguage === l.value ? 'is-active' : ''}
                    style={{ '--lang': l.color }}
                    onClick={() => handleLanguageChange(l.value)}>
                    <span className="epx-seg-dot" />{l.label}{!l.ready && <em>soon</em>}
                  </button>
                ))}
              </div>
              <button type="button" className={`epx-help-btn ${showHelp ? 'is-open' : ''}`} onClick={() => setShowHelp(v => !v)}>
                <i className="fa-solid fa-book-open" /> Variables & help <i className={`fa-solid fa-chevron-${showHelp ? 'up' : 'down'}`} />
              </button>
            </div>

            {showHelp && (
              <div className="epx-help">
                {langMeta(editing.eLanguage).help.map(([k, v]) => (
                  <div key={k}><code>{k}</code><span>{v}</span></div>
                ))}
              </div>
            )}
            {!langMeta(editing.eLanguage).ready && !showHelp && (
              <div className="epx-soon"><i className="fa-solid fa-hourglass-half" /> {langMeta(editing.eLanguage).label} isn’t available on this hosting yet — calls will return <b>501</b> until it ships.</div>
            )}

            <div className="epx-editor">
              <div className="epx-editor-tab">
                <span className="epx-editor-dots"><i /><i /><i /></span>
                <span className="epx-editor-file"><span className="epx-seg-dot" style={{ '--lang': langMeta(editing.eLanguage).color }} />{fileNameFor(editing.eName, editing.eLanguage)}</span>
              </div>
              <div className="epx-editor-area" style={{ '--h': `${editorHeight}px` }}>
                {editing.__loading ? (
                  <div className="epx-editor-loading"><span className="epx-spin" /> Loading code…</div>
                ) : (
                  <Editor
                    height="100%"
                    language={langMeta(editing.eLanguage).monaco}
                    theme="vs-dark"
                    value={editing.eCode}
                    onChange={(value) => setEditing((prev) => ({ ...prev, eCode: value ?? '' }))}
                    loading={<div className="epx-editor-loading"><span className="epx-spin" /> Loading editor…</div>}
                    onMount={(editorInstance, monacoInstance) => {
                      editorRef.current = editorInstance;
                      editorInstance.addCommand(monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.KeyS, () => saveRef.current());
                      editorInstance.addCommand(monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.KeyZ, () => editorInstance.trigger('keyboard', 'undo', null));
                      editorInstance.addCommand(monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.KeyY, () => editorInstance.trigger('keyboard', 'redo', null));
                      editorInstance.addCommand(monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyMod.Shift | monacoInstance.KeyCode.KeyZ, () => editorInstance.trigger('keyboard', 'redo', null));
                    }}
                    options={{
                      fontSize: 13.5,
                      minimap: { enabled: false },
                      automaticLayout: true,
                      scrollBeyondLastLine: false,
                      wordWrap: 'on',
                      tabSize: 4,
                      padding: { top: 12, bottom: 12 },
                      renderLineHighlight: 'all',
                      smoothScrolling: true,
                    }}
                  />
                )}
              </div>
              <div className="epx-editor-status">
                <span className="lang" style={{ '--lang': langMeta(editing.eLanguage).color }}>{langMeta(editing.eLanguage).label}</span>
                <span>UTF-8</span>
                <span className="right">{editing.eCode ? editing.eCode.split('\n').length : 1} lines</span>
                {isDirty ? <span className="dirty">● Unsaved</span> : <span className="saved">✓ Saved</span>}
              </div>
              <div className="epx-editor-resize" onMouseDown={onEditorResizeMouseDown} role="separator" aria-label="Resize editor" title="Drag to resize" />
            </div>

            {editing.eId && editing.url && (
              <div className="epx-ide-foot">
                <span className="epx-url-method">URL</span>
                <code>{editing.url}</code>
                <CopyButton text={editing.url} onCopied={() => showToast('URL copied')} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Test runner ────────────────────────────────────── */}
      {testFor && (
        <div className="epx-overlay is-center" onMouseDown={(e) => { if (e.target === e.currentTarget) setTestFor(null); }}>
          <div className="epx-sheet epx-test" role="dialog" aria-label="Test endpoint">
            <span className="epx-grip" />
            <div className="epx-sheet-head">
              <span className="epx-sheet-ic"><i className="fa-solid fa-flask" /></span>
              <div><h3>Test “{testFor.eName}”</h3><p>Sends a real request from your browser — it shows up in Logs too.</p></div>
              <button type="button" className="epx-icon-btn ghost" onClick={() => setTestFor(null)} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
            </div>
            <div className="epx-sheet-body">
              <div className="epx-test-line">
                <div className="epx-method" role="radiogroup" aria-label="Method">
                  {['GET', 'POST'].map(m => (
                    <button key={m} type="button" role="radio" aria-checked={testReq.method === m}
                      className={testReq.method === m ? 'is-active' : ''} onClick={() => setTestReq(r => ({ ...r, method: m }))}>{m}</button>
                  ))}
                </div>
                <code className="epx-test-url">{testFor.url}</code>
              </div>
              {testReq.method === 'POST' && (
                <div className="epx-field">
                  <label htmlFor="epx-body">JSON body</label>
                  <textarea id="epx-body" className="epx-textarea" rows={5} spellCheck={false}
                    value={testReq.body} onChange={e => setTestReq(r => ({ ...r, body: e.target.value }))} />
                </div>
              )}
              <button type="button" className="epx-btn primary full" onClick={runTest} disabled={testing}>
                {testing ? <><span className="epx-spin" /> Sending…</> : <><i className="fa-solid fa-paper-plane" /> Send request</>}
              </button>

              {testRes && (
                testRes.error ? (
                  <div className="epx-test-err">
                    <b><i className="fa-solid fa-plug-circle-xmark" /> Request failed</b>
                    <span>{testRes.error}. If the endpoint works from other tools, your browser may be blocking it (CORS) — check Logs to see whether the call arrived.</span>
                  </div>
                ) : (
                  <div className="epx-test-res">
                    <div className="epx-test-meta">
                      <span className={`epx-code is-${statusTone(testRes.status)}`}>{testRes.status} {testRes.statusText}</span>
                      <span><i className="fa-regular fa-clock" /> {testRes.ms} ms</span>
                      {testRes.type && <span className="epx-muted">{testRes.type.split(';')[0]}</span>}
                    </div>
                    <CodeBlock title="Response" text={testRes.body} onCopied={() => showToast('Copied')} />
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Logs drawer ────────────────────────────────────── */}
      {logsFor && (
        <div className="epx-overlay is-drawer" onMouseDown={(e) => { if (e.target === e.currentTarget) closeLogs(); }}>
          <aside className="epx-drawer" role="dialog" aria-label={`Logs for ${logsFor.eName}`}>
            <div className="epx-drawer-head">
              <LangTile lang={logsFor.eLanguage} size="sm" />
              <div className="epx-drawer-title">
                <h3>{logsFor.eName}</h3>
                <span>Logs · last 100 calls</span>
              </div>
              <button type="button" className={`epx-live ${liveTail ? 'is-live' : ''}`} onClick={() => setLiveTail(v => !v)}
                title={liveTail ? 'Auto-refreshing every 4s — click to stop' : 'Auto-refresh every 4s'}>
                <span className="epx-live-dot" />{liveTail ? 'Live' : 'Live tail'}
              </button>
              <button type="button" className="epx-icon-btn ghost" onClick={closeLogs} aria-label="Close logs"><i className="fa-solid fa-xmark" /></button>
            </div>

            <div className="epx-drawer-tabs" role="tablist">
              <button type="button" role="tab" aria-selected={logsView === 'calls'} className={logsView === 'calls' ? 'is-active' : ''} onClick={() => switchLogsView('calls')}>
                <i className="fa-solid fa-arrow-right-arrow-left" /> Calls <em>{logs.length}</em>
              </button>
              <button type="button" role="tab" aria-selected={logsView === 'raw'} className={logsView === 'raw' ? 'is-active' : ''} onClick={() => switchLogsView('raw')}>
                <i className="fa-solid fa-file-lines" /> error_log
              </button>
            </div>

            {logsView === 'raw' ? (
              <div className="epx-drawer-body">
                <div className="epx-raw-bar">
                  <p>The persistent <code>error_log</code> file for this endpoint — every <code>error_log()</code> call and PHP warning, across all calls.</p>
                  <div className="epx-raw-actions">
                    <button type="button" className="epx-btn ghost sm" onClick={() => loadRawLog()}><i className="fa-solid fa-rotate" /> Refresh</button>
                    <button type="button" className="epx-btn ghost sm" onClick={() => { copyToClipboard(rawLog || ''); showToast('Error log copied'); }} disabled={!rawLog}><i className="fa-regular fa-copy" /> Copy</button>
                    <button type="button" className="epx-btn ghost sm" onClick={() => downloadText(`${safeName(logsFor.eName)}-error_log.log`, rawLog || '')} disabled={!rawLog}><i className="fa-solid fa-download" /> Download</button>
                    <button type="button" className="epx-btn ghost sm is-danger-text" onClick={handleClearRawLog} disabled={!rawLog}><i className="fa-regular fa-trash-can" /> Clear</button>
                  </div>
                </div>
                {rawLogLoading ? (
                  <div className="epx-drawer-empty"><span className="epx-spin dark" /> Loading error log…</div>
                ) : !rawLog ? (
                  <div className="epx-drawer-empty"><i className="fa-regular fa-face-smile" /><b>error_log is empty</b><span>Nothing has been written to it yet.</span></div>
                ) : (
                  <div className="epx-console tall">
                    {rawLog.split(/\r?\n/).filter((l) => l !== '').map((line, i) => (
                      <div className={`epx-console-line ${/fatal|error/i.test(line) ? 'is-err' : /warning|notice|deprecated/i.test(line) ? 'is-warn' : ''}`} key={i}>
                        <span className="n">{i + 1}</span><span className="m">{line}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="epx-drawer-body">
                {!logsLoading && logs.length > 0 && (
                  <div className="epx-log-summary">
                    <div><b>{logs.length}</b><span>calls</span></div>
                    <div><b className="ok">{okCount}</b><span>OK</span></div>
                    <div><b className={errorCount ? 'err' : ''}>{errorCount}</b><span>errors</span></div>
                    <div><b>{avgMs != null ? `${avgMs}ms` : '—'}</b><span>avg time</span></div>
                  </div>
                )}

                <div className="epx-log-tools">
                  <div className="epx-filter" role="tablist" aria-label="Filter">
                    {[['all', 'All', logs.length], ['ok', 'OK', okCount], ['error', 'Errors', errorCount]].map(([k, l, n]) => (
                      <button key={k} type="button" role="tab" aria-selected={logFilter.status === k}
                        className={`${logFilter.status === k ? 'is-active' : ''} is-${k}`}
                        onClick={() => setLogFilter(f => ({ ...f, status: k }))}>{l} <em>{n}</em></button>
                    ))}
                  </div>
                  <div className="epx-search">
                    <i className="fa-solid fa-magnifying-glass" />
                    <input placeholder="Search body, error, debug log…" value={logFilter.q}
                      onChange={(e) => setLogFilter(f => ({ ...f, q: e.target.value }))} />
                  </div>
                  <button type="button" className="epx-icon-btn" title="Download filtered logs" aria-label="Download logs"
                    onClick={() => downloadText(`${safeName(logsFor.eName)}-logs.log`, filteredLogs.map(formatLogForExport).join('\n\n') || 'No logs match the current filter.')}>
                    <i className="fa-solid fa-download" />
                  </button>
                  <button type="button" className="epx-icon-btn danger" title="Clear logs" aria-label="Clear logs" onClick={handleClearLogs} disabled={!logs.length}>
                    <i className="fa-regular fa-trash-can" />
                  </button>
                </div>

                {logsLoading ? (
                  <div className="epx-drawer-empty"><span className="epx-spin dark" /> Loading logs…</div>
                ) : logs.length === 0 ? (
                  <div className="epx-drawer-empty"><i className="fa-solid fa-satellite-dish" /><b>No calls yet</b><span>Hit Test or call the URL — every request shows up here.</span></div>
                ) : filteredLogs.length === 0 ? (
                  <div className="epx-drawer-empty"><i className="fa-solid fa-filter" /><b>No logs match</b><span>Try a different filter or search.</span></div>
                ) : (
                  <div className="epx-logs">
                    {filteredLogs.map((log) => {
                      const isError = isLogError(log);
                      const isOpen = expandedLog === log.lId;
                      const debugEntries = parseDebugLog(log.lDebugLog);
                      const tone = isError && !log.lStatusCode ? 'err' : statusTone(log.lStatusCode);
                      return (
                        <div key={log.lId} className={`epx-log ${isError ? 'is-error' : ''} ${isOpen ? 'is-open' : ''}`}>
                          <button type="button" className="epx-log-head" onClick={() => setExpandedLog(isOpen ? null : log.lId)} aria-expanded={isOpen}>
                            <span className={`epx-code is-${tone}`}>{log.lStatusCode || 'ERR'}</span>
                            <span className="epx-log-method">{log.lMethod || 'GET'}</span>
                            <span className="epx-log-time" title={formatDate(log.lCreatedAt, true)}>{timeAgo(log.lCreatedAt)}</span>
                            {debugEntries.length > 0 && <span className="epx-log-debug" title="Debug log lines"><i className="fa-solid fa-terminal" /> {debugEntries.length}</span>}
                            {log.lWarnings && <span className="epx-log-warn" title="PHP warnings"><i className="fa-solid fa-triangle-exclamation" /></span>}
                            <span className="epx-log-dur">
                              {log.lDurationMs != null && <><span className="bar"><i style={{ width: `${Math.max(4, (log.lDurationMs / maxMs) * 100)}%` }} /></span>{log.lDurationMs}ms</>}
                            </span>
                            <i className={`fa-solid fa-chevron-down epx-log-chev`} />
                          </button>
                          {isOpen && (
                            <div className="epx-log-body">
                              <div className="epx-log-when">{formatDate(log.lCreatedAt, true)}</div>
                              {log.lError && (
                                <div className="epx-alert">
                                  <i className="fa-solid fa-circle-exclamation" />
                                  <div><b>Error</b><span>{log.lError}</span></div>
                                </div>
                              )}
                              {log.lStackTrace && <CodeBlock title="Stack trace" text={log.lStackTrace} tone="err" onCopied={() => showToast('Copied')} />}

                              {debugEntries.length > 0 && (
                                <div className="epx-block is-console">
                                  <div className="epx-block-head">
                                    <span><i className="fa-solid fa-terminal" /> Debug log · {debugEntries.length} line{debugEntries.length === 1 ? '' : 's'}</span>
                                    <CopyButton text={debugEntries.map(formatDebugLineForExport).join('\n')} onCopied={() => showToast('Debug log copied')} />
                                  </div>
                                  <div className="epx-console">
                                    {debugEntries.map((entry, i) => (
                                      <div className="epx-console-line" key={i}>
                                        <span className="t">{entry.t != null ? `+${entry.t}ms` : '·'}</span>
                                        <span className={`src ${entry.src === 'error_log' ? 'php' : 'app'}`}>{entry.src === 'error_log' ? 'PHP' : 'APP'}</span>
                                        <span className="m">{entry.msg}{entry.data && <pre>{pretty(entry.data)}</pre>}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {log.lWarnings && <CodeBlock title="PHP warnings / notices" text={warningsText(log.lWarnings)} tone="warn" onCopied={() => showToast('Copied')} />}

                              <div className="epx-log-cols">
                                <CodeBlock title="Request body" text={log.lRequestBody} onCopied={() => showToast('Copied')} />
                                <CodeBlock title="Response" text={log.lResponseBody} onCopied={() => showToast('Copied')} />
                              </div>
                              <details className="epx-more">
                                <summary>Query string & headers</summary>
                                <div className="epx-log-cols">
                                  <CodeBlock title="Query string" text={log.lQueryString} onCopied={() => showToast('Copied')} />
                                  <CodeBlock title="Headers" text={log.lHeaders} onCopied={() => showToast('Copied')} />
                                </div>
                              </details>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </aside>
        </div>
      )}

      {/* ── Confirm ──────────────────────────────────────────── */}
      {confirmState && (
        <div className="epx-overlay is-center is-top" onMouseDown={(e) => { if (e.target === e.currentTarget && !confirmState.busy) setConfirmState(null); }}>
          <div className="epx-confirm" role="alertdialog" aria-label={confirmState.title}>
            <span className={`epx-confirm-ic ${confirmState.danger ? 'is-danger' : ''}`}>
              <i className={`fa-solid ${confirmState.danger ? 'fa-triangle-exclamation' : 'fa-circle-question'}`} />
            </span>
            <h3>{confirmState.title}</h3>
            <p>{confirmState.message}</p>
            <div className="epx-confirm-actions">
              <button type="button" className="epx-btn ghost" onClick={() => setConfirmState(null)} disabled={confirmState.busy}>Cancel</button>
              <button type="button" className={`epx-btn ${confirmState.danger ? 'danger' : 'primary'}`} onClick={runConfirm} disabled={confirmState.busy}>
                {confirmState.busy ? <><span className="epx-spin" /> Working…</> : confirmState.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`epx-toast is-${toast.type}`} role="status">
          <i className={`fa-solid ${toast.type === 'error' ? 'fa-circle-xmark' : toast.type === 'info' ? 'fa-circle-info' : 'fa-circle-check'}`} /> {toast.msg}
        </div>
      )}
    </div>
  );
}